import AddCircle from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Radio, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CHECKBOX, FREE_TEXT, horizontalScrollTableStyle, RADIO } from '../../constants';
import { ColumnProps, IdName, RowProps } from '../../models/global';
import { CodeInterface, State } from '../../models/state';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import './SurveyForm.scss';

interface MatriceComponentProps {
  handleTableColumnName: (value: string, colId: string) => void;
  handleMatrixRowName: (value: string, rowId: string) => void;
  handleMatrixRowCode: (value: CodeInterface, rowId: string) => void;
  addColumn: (columnType: string) => void;
  addRow: () => void;
  removeColumn: (colId: string) => void;
  removeRow: (rowId: string) => void;
  changeColumnTypes: (columnType: string) => void;
  matrice: {
    id: string;
    columns: [] | ColumnProps[];
    rows: [] | RowProps[];
  };
  language: string;
  invalidFields: string[];
  readonly?: boolean;
  blockId: string;
  filteredCodes: CodeInterface[];
}

function MatriceComponent(props: MatriceComponentProps) {
  const {
    handleTableColumnName,
    handleMatrixRowName,
    handleMatrixRowCode,
    addColumn,
    addRow,
    removeColumn,
    removeRow,
    changeColumnTypes,
    matrice,
    language,
    invalidFields,
    readonly,
    blockId,
    filteredCodes,
  } = props;
  const [matrixType, setMatrixType] = useState('');
  const { columnTypesMatrice } = useSelector((state: State) => state.survey);
  const { codes } = useSelector((state: State) => state.code);

  useEffect(() => {
    if (matrice?.columns?.length) {
      setMatrixType(matrice?.columns[0].type);
    }
  }, [matrice]);

  const getField = (type: string) => {
    switch (type) {
      case CHECKBOX:
        return <Checkbox disabled />;
      case RADIO:
        return <Radio disabled />;
      case FREE_TEXT:
        return <TextField id='Other' label='Other...' variant='standard' disabled sx={{ ml: 2 }} />;
    }
  };

  const getOtherOptionColumn = () => (matrice?.columns as ColumnProps[])?.find(column => column.type === FREE_TEXT);

  const handleOtherOption = () => {
    const otherOptionColumn = getOtherOptionColumn();
    if (otherOptionColumn) {
      removeColumn(otherOptionColumn.id);
    } else {
      addColumn(FREE_TEXT);
    }
  };

  return (
    !!matrice && (
      <Box sx={{ mt: 5 }} className='table-container' style={{ pointerEvents: 'visible' }}>
        <Box className='column-type-container'>
          <Box width={400} className='flex-center column-type' style={readonly ? { pointerEvents: 'none' } : {}}>
            <Dropdown
              value={matrixType}
              label='Matrix Type'
              options={columnTypesMatrice}
              onChange={(_: SyntheticEvent, value: string) => {
                if (value) {
                  setMatrixType(value);
                  changeColumnTypes(value);
                }
              }}
              disableClearable
              error={!matrixType && invalidFields.includes(`COLUMN_LENGTH_${matrice.id}_${blockId}`)}
              required
            />
            {!!matrixType && (
              <Box
                className='plus-minus-item'
                onClick={() => addColumn(matrixType)}
                mb={'0 !important'}
                style={{
                  border: invalidFields.includes(`COLUMN_LENGTH_${matrice.id}_${blockId}`) ? '1px solid red' : '',
                }}
              >
                <span>Add column</span>
                <AddCircle htmlColor='green' />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              ml: 5,
            }}
            style={readonly ? { pointerEvents: 'none' } : {}}
          >
            <Typography display='inline'>Include other option?</Typography>{' '}
            <Tooltip title='Other option' sx={{ pl: 0 }}>
              <Checkbox onClick={handleOtherOption} checked={!!getOtherOptionColumn()} />
            </Tooltip>
          </Box>
        </Box>
        <TableContainer component={Paper} sx={{ ...horizontalScrollTableStyle, mt: 2.5 }}>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead style={readonly ? { pointerEvents: 'none' } : {}}>
              <TableRow sx={{ pl: 67 }}>
                {matrice?.columns.map((matriceCol: ColumnProps, columnIndex: number) => (
                  <TableCell key={matriceCol.id}>
                    <Box>
                      <TextField
                        multiline
                        variant='standard'
                        value={language === 'English' ? matriceCol.name : `${matriceCol[`name${language}`] || ''}`}
                        onChange={e => handleTableColumnName(e.target.value, matriceCol.id)}
                        error={invalidFields.includes(`COLUMN_${columnIndex}_${matrice.id}_${blockId}`)}
                      />
                      {language !== 'English' && (
                        <Typography className='english-value'>English: {matriceCol.name || 'N/A'}</Typography>
                      )}
                    </Box>
                    <RemoveCircleIcon htmlColor='red' onClick={() => removeColumn(matriceCol.id)} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={readonly ? { pointerEvents: 'none' } : {}}>
              {matrice?.rows.map((row, rowIndex: number) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    pl: 2,
                  }}
                >
                  <Box className='row-info' alignItems={language !== 'English' ? 'start' : 'center'}>
                    {rowIndex === 0 ? (
                      <AddCircle htmlColor='green' onClick={addRow} />
                    ) : (
                      <RemoveCircleIcon htmlColor='red' onClick={() => removeRow(row.id)} />
                    )}
                    <Box className='matrix-name'>
                      <TextField
                        multiline
                        label='Name'
                        variant='outlined'
                        value={language === 'English' ? row.name : `${row[`name${language}`] || ''}`}
                        onChange={e => handleMatrixRowName(e.target.value, row.id)}
                        error={invalidFields.includes(`ROW_${rowIndex}_${matrice.id}_${blockId}`)}
                      />
                      {language !== 'English' && (
                        <Typography className='english-value'>English: {row.name || 'N/A'}</Typography>
                      )}
                    </Box>
                    <Dropdown
                      value={codes.find(code => code.codeId === row.codeId)?.code || ''}
                      label='Code'
                      options={filteredCodes.map(({ code, codeId }) => ({
                        id: codeId,
                        name: code,
                      }))}
                      onChange={(_: SyntheticEvent, value: IdName) =>
                        handleMatrixRowCode(value ? { codeId: Number(value.id), code: value.name } : null, row.id)
                      }
                      error={invalidFields.includes(`CODE_${rowIndex}_${matrice.id}_${blockId}`)}
                    />
                  </Box>
                  {matrice?.columns.map((column: ColumnProps, index: number) => (
                    <TableCell component='td' scope='row' key={index}>
                      {getField(column.type)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  );
}

export default MatriceComponent;
